import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { categoricalColorAt } from "../../../../utils/categoricalColors";
import { type Job, Position } from "../../../../utils/models";
import { useCertificationDefaults } from "./useCertificationDefaults";

/**
 * Department groups for the job surfaces in Department Configuration — the Jobs grid, the Manage-jobs
 * picker and the Integrations tab's Synced Jobs aside all read their buckets, labels and dot colours
 * from here so the three can never disagree about which job sits under which group.
 *
 * The grouping is the global HRIS Job→Position mapping (`/jobs/certification-defaults-merged`), used
 * purely as a visual aid — editing it lives in HRIS Mapping → Roles (DAT-537), never here. It is
 * called a **department group** in every user-facing string: "Position" is an internal HRIS word and
 * the UI must not surface it.
 */

/** Sentinels for the grouped Enabled-Jobs picker (DAT-542). A header row carries a prefixed value so
 *  it can never collide with a real job UUID; OTHER_GROUP buckets jobs with no global mapping. */
export const GROUP_PREFIX = "__group__:";
/** The catalog's no-job sentinel. Hidden from every picker, so it can never be named to a user. */
export const UNKNOWN_JOB_NAME = "unknown";

/**
 * True when `jobId` is that sentinel — "this person has no job", wearing a catalog row.
 *
 * A surface that lists the jobs employees hold has to ask: every picker hides this one, so naming it
 * ("Unknown") shows the reader something they have never seen and cannot act on, and "enable this
 * job" is advice that leads nowhere. Such a row needs its own copy — the remedy is setting the
 * person's job, not enabling a job.
 */
export function isUnknownJobId(jobId: string, jobs: readonly { id: string; name: string }[]): boolean {
	return jobs.some((job) => job.id === jobId && job.name === UNKNOWN_JOB_NAME);
}

export const OTHER_GROUP = "__other__";

/** One department group: its key, its already-translated label, its legend colour and its jobs. */
export type JobGroup = {
	/** The `Position` value, or `OTHER_GROUP` for jobs with no global mapping. */
	key: string;
	label: string;
	/** Legend dot colour — a `CATEGORICAL` dot, fixed per group key so it is stable across surfaces. */
	color: string;
	/** Every job in the catalog under this group, sorted by display name. */
	jobs: Job[];
};

/** Position declaration order minus the DontSync sentinel — the canonical group order, "Other" last. */
const POSITION_ORDER: string[] = Object.values(Position).filter((position) => position !== Position.DontSync);

/**
 * Colour per group key, taken from the app-wide categorical ramp by the group's index in the canonical
 * order. Index-based (not hash-based) so the first groups get the most distinguishable hues, and so a
 * group keeps its colour whether or not the department happens to enable any of its jobs.
 */
export const jobGroupColor = (key: string): string => {
	const index = POSITION_ORDER.indexOf(key);
	// `Other` and anything the enum no longer carries land on the last hue rather than on `undefined`.
	const resolved = index >= 0 ? index : POSITION_ORDER.length;
	return categoricalColorAt(resolved).dot;
};

export type JobGroupsResult = {
	/** Groups in canonical order, each holding at least one job from the catalog. */
	groups: JobGroup[];
	/** `jobId → group key`, for grouping an arbitrary job list (enabled jobs, grid rows). */
	groupKeyByJobId: ReadonlyMap<string, string>;
	groupByKey: ReadonlyMap<string, JobGroup>;
	/** Canonical order of the present group keys — feed straight to `SystemDataGrid`'s `rowGrouping.groupOrder`. */
	groupOrder: string[];
	/** The global mapping is still loading; the groups resolve to a single "Other" bucket until it lands. */
	isLoading: boolean;
};

/** Buckets the whole job catalog into department groups. `allJobs` comes from `useAllJobs()`. */
export function useJobGroups(allJobs: Job[]): JobGroupsResult {
	const { t } = useTranslation();

	const { data: globalMapping = [], isLoading } = useCertificationDefaults();

	const groupKeyByJobId = useMemo(() => {
		const map = new Map<string, string>();
		for (const mapping of globalMapping) {
			if (!mapping.position || mapping.position === Position.DontSync) continue;
			// First non-null position wins — the global table is effectively 1 position per job.
			for (const job of mapping.jobs) if (!map.has(job.id)) map.set(job.id, mapping.position);
		}
		return map;
	}, [globalMapping]);

	const groups = useMemo<JobGroup[]>(() => {
		const buckets = new Map<string, Job[]>();
		for (const job of allJobs) {
			if (job.name === UNKNOWN_JOB_NAME) continue;
			const key = groupKeyByJobId.get(job.id) ?? OTHER_GROUP;
			const bucket = buckets.get(key);
			if (bucket) bucket.push(job);
			else buckets.set(key, [job]);
		}
		return [...POSITION_ORDER, OTHER_GROUP]
			.filter((key) => buckets.has(key))
			.map((key) => ({
				key,
				label: key === OTHER_GROUP ? t("department.config.jobs.ungrouped") : key,
				color: jobGroupColor(key),
				jobs: (buckets.get(key) ?? []).sort((a, b) => a.displayName.localeCompare(b.displayName)),
			}));
	}, [allJobs, groupKeyByJobId, t]);

	const groupByKey = useMemo(() => new Map(groups.map((group) => [group.key, group])), [groups]);
	const groupOrder = useMemo(() => groups.map((group) => group.key), [groups]);

	return { groups, groupKeyByJobId, groupByKey, groupOrder, isLoading };
}
